"use client"
import { useParams } from "react-router-dom"
import { motion } from "framer-motion"
import { Link } from "react-router-dom"

const TeamMemberProfile = () => {
  const { id } = useParams()

  // This would typically come from an API or database
  // For demo purposes, we'll use a static object
  const teamMembers = {
    "nabeel-butt": {
      name: "Nabeel Butt",
      title: "Chief Executive Officer",
      image: "https://www.cs.purdue.edu/cgvlab/www/resources/images/team/butt_nabeel.jpg",
      bio: "With a PhD in Computer Science, Nabeel brings over 18 years of experience in AI and machine learning, previously leading innovation at Meta, Autodesk, Intel and Walt Disney. He founded AlfasTech with a vision to make advanced AI accessible to businesses of all sizes.",
      biography: [
        "Dr. Nabeel Butt is the founder and CEO of AlfasTech, bringing over 18 years of experience in artificial intelligence and machine learning to the company. His visionary leadership has established AlfasTech as a pioneer in enterprise AI solutions across multiple industries.",
        "With a Ph.D. in Computer Science specializing in AI from Stanford University, Nabeel has led innovation teams at some of the world's most prestigious technology companies including Meta, Autodesk, Intel, and Walt Disney. His unique blend of academic rigor and practical industry experience has enabled him to bridge the gap between cutting-edge research and real-world applications.",
        "Throughout his career, Nabeel has led cutting-edge AI innovation in industry, driving the development of machine learning technologies that power real-world applications. He has delivered impactful solutions across natural language processing, computer vision, and autonomous systems that have transformed how businesses operate and serve their customers.",
        "Nabeel founded AlfasTech with a clear vision: to democratize access to advanced AI technologies, making them accessible and practical for businesses of all sizes. Under his leadership, AlfasTech has grown rapidly, helping companies across various industries transform their operations through intelligent automation and data-driven decision making.",
        "Prior to AlfasTech, Nabeel served as the Tech Lead of Meta AI, where he led a team of 20+ researchers and engineers in developing cutting-edge AI solutions that improved content moderation and personalization for millions of Ray Ban Meta users. His work on multimodal AI systems helped establish new standards for wearable technology interfaces.",
        "Nabeel was at the forefront of Meta's Oculus VR/AR manufacturing, leading the integration of advanced AI technologies into core enterprise systems such as ERP, CRM, and HRM platforms. His work focused on building intelligent, connected ecosystems where AI not only streamlines operations but also enhances decision-making. By embedding AI models into these systems, he has enabled real-time data analysis, predictive modeling, and intelligent automation — turning static business tools into dynamic engines for growth.",
        "At Autodesk, Nabeel served as a Lead Principal Engineer, where he developed advanced motion planning algorithms for industry-scale 7-axis robotic arms used in automotive chassis welding. He also spearheaded the design and development of a custom 3D slicing engine and software stack for multi-gantry industrial 3D printers, enabling precise coordination, collision avoidance, and scalable toolpath generation for complex, large-format additive manufacturing.",
        "During his tenure at Intel, Nabeel contributed to the development of next-generation AI accelerator chips, optimizing neural network architectures for edge computing applications. His work helped establish new benchmarks for energy-efficient AI processing in resource-constrained environments.",
        "At Walt Disney Imagineering, Nabeel applied his expertise to create immersive, AI-driven experiences for theme park attractions. His innovations in real-time character animation and responsive environmental systems helped bring beloved characters to life in unprecedented ways.",
        "A passionate advocate for ethical AI development, Nabeel frequently speaks at international conferences on responsible AI implementation and the future of human-AI collaboration. He serves on several advisory boards focused on AI ethics and governance, helping shape policies that ensure AI technologies benefit humanity."
      ],
      education: [
        "Ph.D. in Computer Science, Purdue University",
        "M.S. in Computer Science, University of Waterloo",
        "B.S. in Computer Science, University of Engineering & Technology"
      ],
      achievements: [
        "Published 10+ research papers in top conferences",
        "Holds 2 patents in AR/VR technologies"
      ],
      contact: {
        email: "nabeel@alfastech.com",
        linkedin: "linkedin.com/in/nabeelfb",
        twitter: "@nabeelbutt"
      }
    },
    "umair-mahmood": {
      name: "Umair Mahmood",
      title: "Chief Technology Officer",
      image: "/assets/team/umair.jpg",
      bio: "Umair Mahmood is a visionary technology leader with over 8 years of experience driving innovation in AI agent development, cloud-native architectures, and enterprise-scale software solutions. As a strategic architect of autonomous systems, he has pioneered the integration of cutting-edge tools to build self-optimizing AI agents that redefine customer engagement.",
      education: [
        "M.S. in Computer Science, Carnegie Mellon University",
        "B.S. in Software Engineering, University of Waterloo"
      ],
      experience: [
        "Head of Engineering, AI Solutions Inc. (2019-2022)",
        "Senior Software Architect, Cloud Systems (2016-2019)",
        "Lead Developer, Enterprise Solutions (2014-2016)"
      ],
      publications: [
        "Mahmood, U., et al. (2021). \"Scalable Architecture for Multi-Agent AI Systems in Customer Service.\" International Conference on Software Engineering.",
        "Mahmood, U., et al. (2018). \"Cloud-Native Approaches to AI Service Deployment.\" Journal of Cloud Computing."
      ],
      contact: {
        email: "umair@alfastech.ai",
        linkedin: "linkedin.com/in/umairmahmood",
        twitter: "@umairmahmood"
      }
    },
    "uzair-dar": {
      name: "Uzair Dar",
      title: "Chief Operations Officer",
      image: "/assets/team/uzair.jpg",
      bio: "Uzair Dar is a seasoned COO with a strong track record of leading companies and managing global client collaborations across North America, Europe, and Australia. At AlfasTech, he oversees operations with a focus on efficiency and international growth.",
      education: [
        "MBA, Harvard Business School",
        "B.S. in Business Administration, University of Pennsylvania"
      ],
      experience: [
        "COO, Global Tech Solutions (2018-2022)",
        "Director of Operations, International Business Corp (2015-2018)",
        "Senior Manager, Business Development (2012-2015)"
      ],
      publications: [
        "Dar, U., et al. (2020). \"Operational Excellence in AI-First Companies.\" Harvard Business Review.",
        "Dar, U., et al. (2017). \"Scaling International Operations in Tech Startups.\" Journal of Business Strategy."
      ],
      contact: {
        email: "uzair@alfastech.ai",
        linkedin: "linkedin.com/in/uzairdar",
        twitter: "@uzairdar"
      }
    }
  }

  // Get the member ID from the URL parameter
  const memberId = id || "nabeel-butt" // Default to Nabeel if no ID is provided
  const member = teamMembers[memberId] || teamMembers["nabeel-butt"]

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-background">
            {/* Grid pattern */}
            <div className="absolute inset-0 opacity-20">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(#5F72FF22 1px, transparent 1px), linear-gradient(90deg, #5F72FF22 1px, transparent 1px)",
                  backgroundSize: "40px 40px",
                }}
              ></div>
            </div>

            {/* Animated gradient blobs */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0.3 }}
              animate={{
                scale: [0.8, 1.2, 0.9, 1.1, 0.8],
                opacity: [0.3, 0.4, 0.2, 0.5, 0.3],
                x: ["-10%", "5%", "0%", "-5%", "-10%"],
                y: ["5%", "-5%", "10%", "0%", "5%"],
              }}
              transition={{ duration: 20, repeat: Number.POSITIVE_INFINITY, repeatType: "loop" }}
              className="absolute top-1/4 left-1/4 w-[40vw] h-[40vw] rounded-full bg-accent opacity-20 blur-[100px]"
            />
          </div>
        </div>

        <div className="container-custom relative z-10">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="w-full md:w-1/3"
            >
              <div className="rounded-2xl overflow-hidden border-4 border-white/20 shadow-2xl">
                <img
                  src={member.image || "/placeholder.svg?height=400&width=400"}
                  alt={member.name}
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="w-full md:w-2/3"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 font-futuristic">{member.name}</h1>
              <p className="text-2xl text-accent mb-6">{member.title}</p>
              <p className="text-xl text-white mb-8">{member.bio}</p>
              
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/team"
                  className="px-6 py-3 rounded-full font-bold text-white bg-white/10 hover:bg-white/20 transition-all duration-300 border border-white/30 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Team
                </Link>
                <Link
                  to={`mailto:${member.contact.email}`}
                  className="px-6 py-3 rounded-full font-bold text-white bg-gradient-to-r from-accent to-accent2 shadow-lg hover:shadow-accent/50 hover:scale-105 transition-all duration-300 border-2 border-white/20"
                >
                  Contact {member.name.split(" ")[0]}
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-10 gap-12">
            {/* Biography Section for Nabeel Butt */}
            {memberId === "nabeel-butt" ? (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="md:col-span-7"
              >
                <div className="backdrop-blur-xl bg-white/10 p-8 rounded-2xl border-2 border-white/20 shadow-xl">
                  <h2 className="text-2xl font-bold text-white mb-6 font-futuristic">Biography</h2>
                  <div className="space-y-4">
                    {member.biography.map((paragraph, index) => (
                      <motion.p
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="text-white"
                      >
                        {paragraph}
                      </motion.p>
                    ))}
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="md:col-span-7"
              >
                <div className="backdrop-blur-xl bg-white/10 p-8 rounded-2xl border-2 border-white/20 shadow-xl">
                  <h2 className="text-2xl font-bold text-white mb-6 font-futuristic">Education</h2>
                  <ul className="space-y-4 mb-10">
                    {member.education.map((edu, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className="flex items-start"
                      >
                        <svg className="w-5 h-5 text-accent mt-1 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-white">{edu}</span>
                      </motion.li>
                    ))}
                  </ul>

                  <h2 className="text-2xl font-bold text-white mb-6 font-futuristic">Experience</h2>
                  <ul className="space-y-4">
                    {member.experience.map((exp, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className="flex items-start"
                      >
                        <svg className="w-5 h-5 text-accent mt-1 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                            clipRule="evenodd"
                          />
                          <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                        </svg>
                        <span className="text-white">{exp}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            )}

            {/* Achievements/Publications & Contact */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="md:col-span-3"
            >
              <div className="backdrop-blur-xl bg-white/10 p-8 rounded-2xl border-2 border-white/20 shadow-xl">
                {memberId === "nabeel-butt" ? (
                  <>
                    <h2 className="text-2xl font-bold text-white mb-6 font-futuristic">Achievements</h2>
                    <ul className="space-y-4 mb-10">
                      {member.achievements.map((achievement, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          className="flex items-start"
                        >
                          <svg className="w-5 h-5 text-accent mt-1 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <span className="text-white">{achievement}</span>
                        </motion.li>
                      ))}
                    </ul>

                    <h2 className="text-2xl font-bold text-white mb-6 font-futuristic">Education</h2>
                    <ul className="space-y-4 mb-10">
                      {member.education.map((edu, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          className="flex items-start"
                        >
                          <svg className="w-5 h-5 text-accent mt-1 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                          </svg>
                          <span className="text-white">{edu}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-white mb-6 font-futuristic">Publications</h2>
                    <ul className="space-y-4 mb-10">
                      {member.publications.map((pub, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          className="flex items-start"
                        >
                          <svg className="w-5 h-5 text-accent mt-1 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                          </svg>
                          <span className="text-white">{pub}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </>
                )}

                <h2 className="text-2xl font-bold text-white mb-6 font-futuristic">Contact Information</h2>
                <ul className="space-y-4">
                  <motion.li
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center"
                  >
                    <svg className="w-5 h-5 text-accent mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    <a href={`mailto:${member.contact.email}`} className="text-white hover:text-accent transition-colors">
                      {member.contact.email}
                    </a>
                  </motion.li>
                  <motion.li
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className="flex items-center"
                  >
                    <svg className="w-5 h-5 text-accent mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                      <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                    </svg>
                    <a href={`https://${member.contact.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-white hover:text-accent transition-colors">
                      {member.contact.linkedin}
                    </a>
                  </motion.li>
                  <motion.li
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    className="flex items-center"
                  >
                    <svg className="w-5 h-5 text-accent mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                    <a href={`https://twitter.com/${member.contact.twitter.substring(1)}`} target="_blank" rel="noopener noreferrer" className="text-white hover:text-accent transition-colors">
                      {member.contact.twitter}
                    </a>
                  </motion.li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-accent/30 to-accent2/30 relative overflow-hidden">
        {/* Animated Orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-accent opacity-20 blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-accent2 opacity-20 blur-3xl animate-pulse-slower"></div>

        <div className="container-custom relative z-10">
          <div className="backdrop-blur-lg bg-background/80 border border-white/20 rounded-3xl p-12 md:p-16 max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-bold mb-6 text-white text-center font-futuristic"
            >
              Work With Our Team
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl mb-8 text-white text-center"
            >
              Interested in collaborating with {member.name} and the AlfasTech team? Reach out to discuss how our AI solutions can transform your business.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex justify-center"
            >
              <Link
                to="/contact"
                className="px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-accent to-accent2 shadow-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-accent2/40 text-white border-2 border-white/20"
              >
                Get in Touch
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default TeamMemberProfile
